// WHAT: Personal Settings View Component
// WHY:  Displays personal information settings with privacy notice and placeholders
// DOMAIN: Settings
// DEPENDENCIES: UI components, BirthdateField component, Lucide icons
// RISKS:
//   - NOTE: StatusIndicator is imported but not used (dead import)
//   - NOTE: LucideCalendar is imported but not used (dead import)
//   - NOTE: Placeholder section mentions future features (may become outdated)

import { LucideUser, LucideShieldCheck, LucideSettings } from "lucide-react";

import { Card, Heading, Stack } from "@/components/ui";

import { BirthdateField } from "../components/BirthdateField";

/**
 * WHAT:
 *   Settings view component for managing personal information.
 *
 * WHY:
 *   Provides interface for personal data configuration with privacy information.
 *
 * BEHAVIOR:
 *   - Displays header section with user icon and description
 *   - Shows BirthdateField component for date configuration
 *   - Displays privacy notice with security information
 *   - Shows placeholder section for future personal settings
 *   - Uses TracQ branding colors and styling
 *
 * RISKS:
 *   - NOTE: StatusIndicator and LucideCalendar imports are unused (dead code)
 *   - NOTE: Placeholder section may become outdated if features are added elsewhere
 *   - NOTE: Privacy notice text is hardcoded (not from legal/compliance source)
 *
 * @returns {JSX.Element} Personal settings view component
 *
 */
export function PersonalSettingsView() {
  return (
    <Stack direction="col" gap={6}>
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <LucideUser className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Personal Information
            </Heading>
            <p className="text-slate-300">
              Manage your personal information and preferences for a personalized experience.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Birthdate Configuration */}
      <BirthdateField />

      {/* Privacy Information */}
      <Card
        className="border-tracq-deep-blue/30 bg-tracq-deep-blue/10"
        padding="lg"
        variant="default"
      >
        <Stack align="start" direction="row" gap={4}>
          <LucideShieldCheck className="w-6 h-6 text-tracq-bright-blue flex-shrink-0" />
          <Stack direction="col" gap={2}>
            <Heading className="text-lg font-medium text-white" level={3}>
              Privacy Notice
            </Heading>
            <p className="text-slate-300 text-sm">
              Your personal information is stored securely and used only to enhance your TracQ
              experience. We do not share your data with third parties without your explicit
              consent.
            </p>
            <div className="text-slate-400 text-xs space-y-1">
              <p>• All data is encrypted and stored locally</p>
              <p>• No personal information is transmitted to external servers</p>
              <p>• You can delete your data at any time</p>
            </div>
          </Stack>
        </Stack>
      </Card>

      {/* Placeholder for other personal settings */}
      <Card className="bg-slate-800/20 border-slate-700/30" padding="lg" variant="default">
        <Stack align="start" direction="row" gap={4}>
          <LucideSettings className="w-6 h-6 text-slate-400 flex-shrink-0" />
          <Stack direction="col" gap={2}>
            <Heading className="text-lg font-medium text-white" level={3}>
              Additional Personal Settings
            </Heading>
            <p className="text-slate-400 text-sm italic">
              More personal settings will be available here soon, such as:
            </p>
            <div className="text-slate-500 text-xs space-y-1">
              <p>• Preferred name and display options</p>
              <p>• Timezone and locale preferences</p>
              <p>• Notification preferences</p>
              <p>• Theme and appearance settings</p>
            </div>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
